<template>
  <div class="products">
    <AnnotationHeader
      :plan-name="billingStore.planName"
      :formatted-renew-date="billingStore.formattedRenewDate"
      :is-client="teamStore.currentTeamIsClient"
      :is-locked-out="billingStore.isLockedOut"
      :locked-out-reasons="billingStore.lockedOutReasons"
      @billing-error="emit('billing-error', $event)"
    />
    <NestedCard class="products__credits">
      <template #header-title> Workspace credits </template>
      <template #header-subtitle> {{ formattedCreditValue }} credits per billing cycle </template>
      <template #content>
        <Card bordered>
          <CreditSkeleton v-if="loading" />
          <CreditSummary
            v-else-if="creditUsed !== null"
            :available="creditAvailable"
            :used="creditUsed"
            :values="allUsageInfo"
            :loading="loading"
          />
          <template
            v-if="showLimitSettings"
            #footer
          >
            <Limit
              :child-teams-limit-map="childTeamsLimitMap"
              type="creditLimit"
              :max-limit="creditAvailable ?? undefined"
              :disabled="isLoading"
              @save="updateUsageLimit(ProductType.AnnotationCredits, $event)"
            >
              <template #title>Team credit limits</template>
              <template #subtitle>Set credit limits for individual child teams.</template>
            </Limit>
          </template>
        </Card>
      </template>
    </NestedCard>
    <NestedCard class="products__storage">
      <template #header-title> Dataset file management </template>
      <template #header-subtitle>
        {{ formattedStorageValue }} files available per billing cycle
      </template>
      <template #content>
        <Card bordered>
          <CreditSkeleton v-if="loading" />
          <StorageSummary
            v-else-if="storageUsed !== null"
            :available="storageAvailable"
            :used="storageUsed"
            :values="allUsageInfo"
            :loading="loading"
          />
          <template
            v-if="showLimitSettings"
            #footer
          >
            <Limit
              :child-teams-limit-map="childTeamsLimitMap"
              type="storageLimit"
              :max-limit="storageAvailable ?? undefined"
              :disabled="isLoading"
              @save="updateUsageLimit(ProductType.Storage, $event)"
            >
              <template #title>File limits</template>
              <template #subtitle>Set file limits for individual child teams.</template>
            </Limit>
          </template>
        </Card>
      </template>
    </NestedCard>
    <NestedCard
      v-if="!teamStore.currentTeamIsClient"
      class="products__documentation"
      :bordered="false"
      :shadowed="false"
      transparent
      tinted
    >
      <template #header-title> Understand how storage and credits work </template>
      <template #header-subtitle>
        <div class="products__documentation-text">
          Get a clear overview of how your team's files and annotation time are measured in V7.
        </div>
      </template>
      <template #content>
        <DarwinButton
          size="md"
          kind="secondary"
          flair="rounded"
          @click="openDocumentation"
        >
          Read the documentation
        </DarwinButton>
      </template>
    </NestedCard>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useTeamStore } from '@/pinia/useTeamStore'
import AnnotationHeader from './CBBAnnotationHeader.vue'
import CreditSummary from './CBBCreditSummary.vue'
import StorageSummary from './CBBStorageSummary.vue'
import CreditSkeleton from './CBBCreditSkeleton.vue'
import Limit from './CBBLimit.vue'
import NestedCard from '@/uiKit/Card/NestedCardV2.vue'
import Card from '@/uiKit/Card/CardV2.vue'
import DarwinButton from '@/uiKit/Button/DarwinButton.vue'
import { formatLongNumericString } from '@/core/utils/formatter'
import { useCreditUsageLoader } from './useCreditUsageLoader'
import { useCreditMetrics } from './useCreditMetrics'
import { useBillingStore } from './useBillingStore'
import { useToast } from '@/uiKit/Toast/useToast'
import { useClientLimits } from './useClientLimits'
import { ProductType } from './types'

/**
 * Main billing component that displays credit usage and storage information
 * for both parent teams and their clients.
 *
 * @emits billing-error - Emitted when there's an error related to billing operations
 */

const emit = defineEmits<{
  (event: 'billing-error', value: unknown): void
}>()

const billingStore = useBillingStore()
const clientLimits = useClientLimits()
const creditMetrics = useCreditMetrics()
const {
  creditUsed,
  storageUsed,
  creditAvailable,
  storageAvailable,
  childTeamsLimitMap,
  allUsageInfo,
  hasChildTeams,
} = creditMetrics
const creditUsageLoader = useCreditUsageLoader()
const { loading } = creditUsageLoader
const teamStore = useTeamStore()
const toast = useToast()

const isLoading = ref(false)

/**
 * Formats the credit value for display
 */
const formattedCreditValue = computed(() =>
  formatLongNumericString(`${creditAvailable.value}`, ','),
)

/**
 * Formats the storage limit for display
 */
const formattedStorageValue = computed(() =>
  formatLongNumericString(`${storageAvailable.value}`, ','),
)

const showLimitSettings = computed(
  () => hasChildTeams.value && !billingStore.isLockedOut && !teamStore.currentTeamIsClient,
)

const updateUsageLimit = async (
  type: ProductType.AnnotationCredits | ProductType.Storage,
  payload: {
    clientSlug: string
    partnerId: number
    value: number
  },
): Promise<void> => {
  const { clientSlug, partnerId, value } = payload
  isLoading.value = true
  const result = await clientLimits.set({
    clientSlug,
    partnerId,
    value,
    type,
  })

  isLoading.value = false

  if (!result.ok) {
    toast.warning({ meta: { title: result.error.message.toString() } })
    return
  }

  toast.success({ meta: { title: 'Limit saved. Changes will appear shortly.' } })
}

const openDocumentation = (): void => {
  window.open('https://docs.v7labs.com/docs/how-credits-are-calculated')
}

onMounted(() => creditMetrics.init())
</script>

<style lang="scss" scoped>
@import '@/uiKit/assets/fonts.scss';

.products {
  @include col;
  row-gap: 20px;

  &__credits {
    z-index: 3;
  }

  &__storage {
    z-index: 2;
  }

  &__documentation {
    z-index: 1;

    &-text {
      max-width: 300px;
    }
  }
}
</style>
